import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"
import "cropperjs/dist/cropper.css"

const LONG_EDGE = 1200

// Single cover image crop with a locked aspect ratio (benefits 1:1, events 16:9).
// Empty dropzone (click/drop) → crop modal. Filled frame → edit crop or remove.
export default class extends Controller {
  static targets = [
    "picker",
    "input",
    "cropField",
    "removeFlag",
    "modal",
    "cropImage",
    "preview",
    "emptyState",
    "filledState",
    "editButton"
  ]

  static values = {
    aspectRatio: { type: Number, default: 1 },
    outputSize: { type: Number, default: LONG_EDGE },
    paramPrefix: { type: String, default: "image" }
  }

  connect () {
    this.cropper = null
    this.currentSourceName = "image.jpg"
    this.currentOriginalFile = null
    this.retainedOriginalFile = null
    this.pendingCropData = null
    this.editingExisting = false
  }

  disconnect () {
    this.destroyCropper()
  }

  openPicker (event) {
    event.preventDefault()
    this.pickerTarget.click()
  }

  dragover (event) {
    event.preventDefault()
    event.stopPropagation()
    this.emptyStateTarget.classList.add("border-accent")
  }

  dragleave (event) {
    event.preventDefault()
    event.stopPropagation()
    this.emptyStateTarget.classList.remove("border-accent")
  }

  drop (event) {
    event.preventDefault()
    event.stopPropagation()
    this.emptyStateTarget.classList.remove("border-accent")

    const file = Array.from(event.dataTransfer?.files || []).find((item) => item.type.startsWith("image/"))
    if (!file) return

    this.beginNewFile(file)
  }

  select (event) {
    const file = Array.from(event.target.files || []).find((item) => item.type.startsWith("image/"))
    event.target.value = ""
    if (!file) return

    this.beginNewFile(file)
  }

  beginNewFile (file) {
    this.editingExisting = false
    this.pendingCropData = null
    this.currentOriginalFile = file
    this.openCropperFromFile(file)
  }

  edit (event) {
    event.preventDefault()

    if (this.retainedOriginalFile) {
      this.editingExisting = false
      this.pendingCropData = this.parseCropJson(this.hasCropFieldTarget ? this.cropFieldTarget.value : null)
      this.currentOriginalFile = this.retainedOriginalFile
      this.openCropperFromFile(this.retainedOriginalFile)
      return
    }

    const button = this.hasEditButtonTarget ? this.editButtonTarget : event.currentTarget
    const originalUrl = button.dataset.originalUrl
    const filename = button.dataset.filename || "image.jpg"
    if (!originalUrl) {
      this.openPicker(event)
      return
    }

    this.editingExisting = true
    this.currentOriginalFile = null
    this.pendingCropData = this.parseCropJson(button.dataset.cropJson)
    this.openCropper(originalUrl, filename)
  }

  remove (event) {
    event.preventDefault()
    this.clearFileInput()
    this.retainedOriginalFile = null
    this.currentOriginalFile = null
    if (this.hasCropFieldTarget) this.cropFieldTarget.value = ""
    if (this.hasRemoveFlagTarget) this.removeFlagTarget.value = "1"
    if (this.hasPreviewTarget) {
      this.previewTarget.removeAttribute("src")
      this.previewTarget.classList.add("hidden")
    }
    if (this.hasEditButtonTarget) {
      delete this.editButtonTarget.dataset.originalUrl
      delete this.editButtonTarget.dataset.filename
      delete this.editButtonTarget.dataset.cropJson
    }
    this.showEmpty()
    this.dispatchChanged(null)
  }

  cancelCrop (event) {
    event?.preventDefault()
    this.closeModal()
    this.currentOriginalFile = null
    this.editingExisting = false
    this.pendingCropData = null
  }

  async confirmCrop (event) {
    event.preventDefault()
    if (!this.cropper) return

    const data = this.cropper.getData(true)
    const crop = {
      x: Math.round(data.x),
      y: Math.round(data.y),
      width: Math.round(data.width),
      height: Math.round(data.height),
      ratio: this.aspectRatioValue
    }

    const { width, height } = this.outputDimensions()
    const canvas = this.cropper.getCroppedCanvas({
      width,
      height,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high"
    })
    const previewUrl = canvas ? canvas.toDataURL("image/jpeg", 0.92) : null

    if (this.currentOriginalFile) {
      const transfer = new DataTransfer()
      transfer.items.add(this.currentOriginalFile)
      this.inputTarget.files = transfer.files
      this.retainedOriginalFile = this.currentOriginalFile
      if (this.hasRemoveFlagTarget) this.removeFlagTarget.value = "0"
      if (this.hasEditButtonTarget) {
        delete this.editButtonTarget.dataset.originalUrl
        this.editButtonTarget.dataset.filename = this.currentOriginalFile.name
        delete this.editButtonTarget.dataset.cropJson
      }
    } else {
      this.clearFileInput()
      if (this.hasEditButtonTarget && this.hasCropFieldTarget) {
        this.editButtonTarget.dataset.cropJson = JSON.stringify(crop)
      }
    }

    if (this.hasCropFieldTarget) this.cropFieldTarget.value = JSON.stringify(crop)

    if (previewUrl && this.hasPreviewTarget) {
      this.previewTarget.src = previewUrl
      this.previewTarget.classList.remove("hidden")
    }
    this.showFilled()

    this.closeModal()
    this.currentOriginalFile = null
    this.editingExisting = false
    this.pendingCropData = null
    if (previewUrl) this.dispatchChanged(previewUrl)
  }

  openCropperFromFile (file) {
    const reader = new FileReader()
    reader.onload = () => this.openCropper(reader.result, file.name)
    reader.readAsDataURL(file)
  }

  openCropper (src, filename) {
    this.currentSourceName = filename
    this.cropImageTarget.src = src
    this.modalTarget.classList.remove("hidden")
    this.modalTarget.classList.add("flex", "items-center", "justify-center")
    this.modalTarget.setAttribute("aria-hidden", "false")

    this.destroyCropper()
    requestAnimationFrame(() => {
      const cropData = this.pendingCropData
      this.cropper = new Cropper(this.cropImageTarget, {
        aspectRatio: this.aspectRatioValue,
        viewMode: 1,
        dragMode: "move",
        autoCropArea: 1,
        responsive: true,
        background: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        ready: () => {
          if (!cropData || !this.cropper) return
          this.cropper.setData({
            x: cropData.x,
            y: cropData.y,
            width: cropData.width,
            height: cropData.height
          })
          this.pendingCropData = null
        }
      })
    })
  }

  closeModal () {
    this.destroyCropper()
    this.cropImageTarget.removeAttribute("src")
    this.modalTarget.classList.add("hidden")
    this.modalTarget.classList.remove("flex", "items-center", "justify-center")
    this.modalTarget.setAttribute("aria-hidden", "true")
  }

  destroyCropper () {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
  }

  clearFileInput () {
    const transfer = new DataTransfer()
    this.inputTarget.files = transfer.files
  }

  showEmpty () {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    if (this.hasFilledStateTarget) this.filledStateTarget.classList.add("hidden")
  }

  showFilled () {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    if (this.hasFilledStateTarget) this.filledStateTarget.classList.remove("hidden")
  }

  parseCropJson (raw) {
    if (!raw) return null

    try {
      const data = typeof raw === "string" ? JSON.parse(raw) : raw
      if (!data || typeof data !== "object") return null
      return data
    } catch (_error) {
      return null
    }
  }

  outputDimensions () {
    const ratio = this.aspectRatioValue || 1
    const longEdge = this.outputSizeValue

    if (ratio >= 1) {
      return {
        width: longEdge,
        height: Math.round(longEdge / ratio)
      }
    }

    return {
      width: Math.round(longEdge * ratio),
      height: longEdge
    }
  }

  dispatchChanged (dataUrl) {
    this.element.dispatchEvent(new CustomEvent("cover-image-crop:changed", {
      bubbles: true,
      detail: { dataUrl }
    }))
  }
}
